import { Dispatch, useMemo, useReducer } from 'react';

import { isEqual } from 'lodash';

import { isPropertyInListMode } from 'src/helpers/PropertySourceHelper';
import { IProperty } from 'src/interfaces/Property';
import { URL_SET_SOURCES } from 'src/types/UrlSetSources';

import {
	PropertySettingsAction,
	propertySettingsReducer,
	PropertySettingsState
} from '../../state';

interface UsePropertySettingsEditorParams {
	initialProperty: IProperty;
	sourceMode: URL_SET_SOURCES;
	isNew: boolean;
}

interface UsePropertySettingsEditorReturn {
	state: PropertySettingsState;
	dispatch: Dispatch<PropertySettingsAction>;
	isDirty: boolean;
	isListMode: boolean;
}

function createInitialState({
	initialProperty,
	sourceMode,
	isNew
}: UsePropertySettingsEditorParams): PropertySettingsState {
	return {
		draft: initialProperty,
		original: isNew ? null : initialProperty,
		sourceMode,
		errors: [],
		isLoading: false,
		isSaving: false
	};
}

export function usePropertySettingsEditor(
	params: UsePropertySettingsEditorParams
): UsePropertySettingsEditorReturn {
	const [state, dispatch] = useReducer(propertySettingsReducer, params, createInitialState);

	const isDirty = useMemo(
		() => state.draft !== null && !isEqual(state.draft, state.original),
		[state.draft, state.original]
	);

	const isListMode = useMemo(() => isPropertyInListMode(state.sourceMode), [state.sourceMode]);

	return { state, dispatch, isDirty, isListMode };
}
